/**
 * Authentication store - manages user session state
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Define Organization type locally to avoid circular dependency with @services/api
// (api/client.ts imports authStore, so authStore can't import from api)
interface OrganizationSettings {
  defaultProjectVisibility?: 'public' | 'private';
  allowMemberInvites?: boolean;
  requireEmailVerification?: boolean;
  defaultTaskDueDays?: number;
  defaultBoardView?: 'kanban' | 'list' | 'timeline' | 'calendar';
  allowPublicBoards?: boolean;
  requireTwoFactor?: boolean;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  tier: 'free' | 'pro' | 'business' | 'enterprise';
  memberCount: number;
  settings?: OrganizationSettings;
  createdAt: string;
  updatedAt: string;
}

interface NotificationSettings {
  email: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskCompleted: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
    weeklyDigest: boolean;
  };
  push: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
  };
  inApp: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskCompleted: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
  };
}

interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  locale: string;
  theme: 'light' | 'dark' | 'system';
  timezone: string;
  twoFactorEnabled?: boolean;
  notificationSettings?: NotificationSettings;
  organization_id?: string;
  isSuperAdmin?: boolean;
}

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentOrgId: string | null;
  currentOrganization: Organization | null;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setCurrentOrg: (orgId: string | null) => void;
  setCurrentOrganization: (org: Organization | null) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      currentOrgId: null,
      currentOrganization: null,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: user !== null,
          isLoading: false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),

      setCurrentOrg: (orgId) =>
        set({
          currentOrgId: orgId,
        }),

      setCurrentOrganization: (org) =>
        set({
          currentOrganization: org,
          currentOrgId: org?.id ?? null,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          currentOrgId: null,
          currentOrganization: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'neokanban-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist tokens and current org
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        currentOrgId: state.currentOrgId,
      }),
      onRehydrateStorage: () => (state) => {
        // After rehydration, if we have tokens but no user, we're still loading
        // If we have no tokens, we're not loading
        if (state && !state.accessToken) {
          state.isLoading = false;
        }
      },
    }
  )
);

// Register store globally for API client to access (avoids circular dependency)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__NEOKANBAN_AUTH_STORE__ = useAuthStore;
